import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import GlobalStyles from '@mui/material/GlobalStyles';
import { styled } from '@mui/material/styles';
import { getCompanyInfo } from '../../../services/migration/companyDataService';
import { useTranslation } from 'react-i18next';

// Fatura arayüzünü Invoice.tsx'den import edelim (veya burada yeniden tanımlayalım)
// Şimdilik Invoice.tsx'deki Invoice arayüzünü referans alarak temel bir yapı oluşturalım.
// Eğer Invoice.tsx'deki Invoice arayüzü export edilmemişse, gerekli alanları burada tanımlamamız gerekebilir.
// İdeal olanı Invoice.tsx'deki arayüzü export edip burada kullanmaktır.
interface InvoiceItem {
  itemName: string; // Normalde item.name idi, modal için daha açık bir isim
  quantity: number;
  unit: string;
  unitPrice: number; // Normalde item.price idi
  vatRate: number; // Yüzde olarak (örn. 18)
  sctRate?: number; // Yüzde olarak (örn. 10)
  discountRate?: number; // Yüzde olarak (örn. 5)
  lineTotal: number; // Miktar * Birim Fiyat (İndirimler düşülmeden KDV hariç) -> Bu aslında item.total olabilir
  vatAmount: number;
  sctAmount?: number;
  grossTotal: number; // Her şey dahil satır toplamı -> Bu da item.total olabilir, yapısına bakmak lazım
}

interface BranchInfoData {
  name: string;
  taxNumber?: string;
  taxOffice?: string;
  address?: string;
  phone?: string;
  email?: string;
  mersisNo?: string;
  tradeRegistryNo?: string;
  bankAccounts?: Array<{bankName: string; iban: string;}>;
}

interface InvoiceData {
  id: string;
  invoiceNumber: string;
  account: {
    companyName: string;
    taxNumber?: string;
    taxOffice?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  date: string;
  dueDate?: string;
  items: InvoiceItem[]; // Invoice.tsx'deki items any[] idi, burada daha spesifik tanımlıyoruz
  notes?: string;
  subtotal: number; // KDV Hariç Ara Toplam (İndirimler Düşülmüş)
  vatTotal: number;
  sctTotal?: number;
  discountTotal?: number; // Genel İndirim Tutarı
  grandTotal: number;
  // ✅ Tevkifat / Stopaj kesintileri (kağıt fatura görünümü için)
  tevkifatAmount?: number;
  stopajAmount?: number;
  tevkifatRate?: string; // '4/10' vb.
  stopajRate?: number;   // % oran
  originalInvoiceTotal?: number; // ✅ YENİ: Orijinal fatura tutarı (yuvarlama farkları için)
  currency: string;
  /** Fatura kesim anındaki TL kuru — TRY dışı faturada TL karşılığı satırı için */
  exchangeRate?: number;
  type: 'sales' | 'purchase' | 'quick_sale'; // Fatura tipi
  isClosed?: boolean;
  description?: string; // Hızlı satış açıklaması gibi ek notlar
  branchInfo?: BranchInfoData; // Artık zorunlu değil, Firebase'den çekilecek
  createdAt?: string; // Fatura oluşturma/düzenleme zamanı (veritabanından geliyor)
  sourceOfferId?: string; // ✅ YENİ: Kaynak Teklif ID'si (tekliften oluşturulmuşsa)
  sourceOfferNumber?: string; // ✅ YENİ: Kaynak Teklif Numarası (tekliften oluşturulmuşsa)

  // ✅ Seller Snapshot — fatura kesilirken Invoices tablosuna yazılan satıcı bilgileri.
  // Eğer doluysa (yeni faturalar) Firebase yerine bu değerler gösterilir;
  // boşsa (eski faturalar) Firebase'deki global firma bilgisi fallback olarak kullanılır.
  sellerCompanyName?: string | null;
  sellerTaxNumber?: string | null;
  sellerTaxOffice?: string | null;
  sellerAddress?: string | null;
  sellerPhone?: string | null;
  sellerEmail?: string | null;
  sellerWebsite?: string | null;
  sellerMersisNo?: string | null;
  sellerTradeRegistryNo?: string | null;
  isFranchiseInvoice?: boolean;
  sellerSource?: 'Company' | 'BranchFirm' | string | null;
  sellerBranchId?: number | null;
  // Invoice.tsx'den gelen diğer alanlar eklenebilir:
  // branchId?: string | null;
  // status?: 'draft' | 'completed' | 'canceled';
  // exchangeRate?: number;
  // transactionType?: string; // Bu 'type' ile aynı olabilir veya daha detaylı bilgi içerebilir
}

interface InvoiceDisplayModalProps {
  open: boolean;
  onClose: () => void;
  invoice: InvoiceData | null;
  companyId: string | null;
  loading?: boolean;
  extraHeaderContent?: React.ReactNode;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '6px 8px',
  fontSize: '0.8rem',
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.grey[200],
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  fontSize: '1rem',
  color: theme.palette.primary.main,
}));

const formatCurrency = (amount: number | undefined, currencyCode: string = 'TRY', localeCode: string = 'tr-TR'): string => {
  if (typeof amount !== 'number') return '-';
  return new Intl.NumberFormat(localeCode, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateString: string | undefined, localeCode: string = 'tr-TR'): string => {
  if (!dateString) return '-';
  try {
    // ISO formatında bir tarih mi kontrol et (T ve Z içeriyor mu?)
    if (dateString.includes('T') && dateString.includes('Z')) {
      // "2025-06-03T06:26:58.262Z" formatını parçalara ayır
      const [tarihKismi, saatKismi] = dateString.split('T');
      
      // Tarih kısmını Türkçe formatına çevir (yyyy-MM-dd -> dd.MM.yyyy)
      const [yil, ay, gun] = tarihKismi.split('-');
      const formatliTarih = `${gun}.${ay}.${yil}`;
      
      return formatliTarih;
    }
    
    // ISO formatında değilse normal tarih dönüşümü yap
    const date = new Date(dateString);
    // Tarih geçerli mi kontrolü
    if (isNaN(date.getTime())) {
        return dateString; // Geçersizse orijinal dizeyi döndür
    }
    
    // Saat bilgisini 00:00:00 olarak sıfırla
    // Bu saat bilgisi çevirisi nedeniyle oluşabilecek 
    // UTC veya zaman dilimi farklılıklarını önler
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    
    return new Intl.DateTimeFormat(localeCode, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(localDate);
  } catch (e) {
    console.error("Tarih formatı hatası:", e);
    return dateString; // Hata durumunda orijinal dizeyi döndür
  }
};

// Sayıyı Türkçe yazıya çevir
const numberToTurkishText = (num: number): string => {
  if (num === 0) return 'Sıfır';
  
  const ones = ['', 'Bir', 'İki', 'Üç', 'Dört', 'Beş', 'Altı', 'Yedi', 'Sekiz', 'Dokuz'];
  const tens = ['', 'On', 'Yirmi', 'Otuz', 'Kırk', 'Elli', 'Altmış', 'Yetmiş', 'Seksen', 'Doksan'];
  const hundreds = ['', 'Yüz', 'İkiYüz', 'ÜçYüz', 'DörtYüz', 'BeşYüz', 'AltıYüz', 'YediYüz', 'SekizYüz', 'DokuzYüz'];
  
  const scales = [
    { value: 1000000000, name: 'Milyar' },
    { value: 1000000, name: 'Milyon' },
    { value: 1000, name: 'Bin' }
  ];
  
  if (num < 10) return ones[num];
  if (num < 100) {
    const ten = Math.floor(num / 10);
    const one = num % 10;
    return tens[ten] + ones[one];
  }
  if (num < 1000) {
    const hundred = Math.floor(num / 100);
    const remainder = num % 100;
    return hundreds[hundred] + (remainder > 0 ? numberToTurkishText(remainder) : '');
  }
  
  // 1000 ve üzeri
  for (const scale of scales) {
    if (num >= scale.value) {
      const quotient = Math.floor(num / scale.value);
      const remainder = num % scale.value;
      
      let result = '';
      if (scale.value === 1000 && quotient === 1) {
        result = 'Bin'; // "Bir Bin" yerine sadece "Bin"
      } else {
        result = numberToTurkishText(quotient) + scale.name;
      }
      
      if (remainder > 0) {
        result += numberToTurkishText(remainder);
      }
      
      return result;
    }
  }
  
  return '';
};

// Tutarı yazıya çevir
const amountToText = (amount: number, currency: string, isTr: boolean, localeCode: string): string => {
  if (!isTr) {
    return `${new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)} only`;
  }
  const integerPart = Math.floor(amount);
  const decimalPart = Math.round((amount - integerPart) * 100);
  
  let text = numberToTurkishText(integerPart);
  
  // Para birimi adı
  const currencyNames: { [key: string]: { major: string; minor: string } } = {
    'TRY': { major: 'Türk Lirası', minor: 'Kuruş' },
    'USD': { major: 'Amerikan Doları', minor: 'Sent' },
    'EUR': { major: 'Euro', minor: 'Sent' },
    'GBP': { major: 'İngiliz Sterlini', minor: 'Peni' }
  };
  
  const currencyName = currencyNames[currency] || { major: currency, minor: 'Kuruş' };
  
  text += ' ' + currencyName.major;
  
  if (decimalPart > 0) {
    text += ' ' + numberToTurkishText(decimalPart) + ' ' + currencyName.minor;
  }
  
  return text;
};

const formatDateTime = (dateTimeString: string | undefined, localeCode: string = 'tr-TR'): string => {
  if (!dateTimeString) return '-';
  try {
    // ISO formatında bir tarih mi kontrol et (T ve Z içeriyor mu?)
    if (dateTimeString.includes('T') && dateTimeString.includes('Z')) {
      // "2025-06-03T06:26:58.262Z" formatını parçalara ayır
      const [_, saatKismi] = dateTimeString.split('T');
      
      // Saat kısmını milisaniye olmadan al (06:26:58.262Z -> 06:26:58)
      const formatliSaat = saatKismi.split('.')[0];
      
      return formatliSaat;
    }
    
    const date = new Date(dateTimeString);
    // Geçerli bir tarih mi kontrol et
    if (isNaN(date.getTime())) {
      return '-';
    }
    
    return date.toLocaleTimeString(localeCode, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (e) {
    console.error("Saat formatı hatası:", e);
    return '-';
  }
};

const printStyles = `
  @media print {
    body > *:not(.MuiDialog-root),
    .MuiDialog-root > *:not(.MuiDialog-container),
    .MuiDialog-container > *:not(.MuiPaper-root),
    .MuiDialog-paper > *:not(.MuiDialogContent-root),
    .MuiDialogTitle-root,
    .MuiDialogActions-root {
      display: none !important;
    }

    .MuiDialog-root,
    .MuiDialog-container,
    .MuiDialog-paper,
    .MuiDialogContent-root {
      display: block !important; /* Ensure these are block for layout */
      width: auto !important;
      height: auto !important;
      overflow: visible !important;
      background: transparent !important;
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    #printableInvoiceContent {
      width: 210mm !important; 
      min-height: 290mm !important; /* A4 yüksekliğine yakın bir değer, içeriğe göre ayarlanabilir */
      padding: 10mm !important; /* Fatura içi kenar boşlukları */
      margin: 0 auto !important; /* Sayfada ortala */
      box-shadow: none !important;
      border: none !important;
      background-color: white !important;
      page-break-before: auto !important;
      page-break-after: auto !important;
      font-size: 10pt; /* Yazdırma için okunabilir bir font boyutu */
    }

    @page {
      size: A4;
      margin: 0;
    }

    #printableInvoiceContent .MuiPaper-root { /* Fatura içindeki Paper componentleri */
      box-shadow: none !important;
      border: 1px solid #ccc !important;
      background-color: white !important;
    }

    #printableInvoiceContent table {
      width: 100% !important;
      page-break-inside: auto;
    }
    #printableInvoiceContent tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    #printableInvoiceContent thead {
      display: table-header-group;
    }
  }
`;

const InvoiceDisplayModal: React.FC<InvoiceDisplayModalProps> = ({
  open,
  onClose,
  invoice,
  companyId,
  loading = false,
  extraHeaderContent,
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [fetchedBranchInfo, setFetchedBranchInfo] = useState<BranchInfoData | null>(null);
  const [loadingBranchInfo, setLoadingBranchInfo] = useState<boolean>(false);
  const [branchInfoError, setBranchInfoError] = useState<string | null>(null);

  // ✅ Faturanın kendi seller snapshot'ı var mı? (PR1+ ile yazılan yeni faturalar)
  // Snapshot dolu ise Firebase'den global firma bilgisi okumaya GEREK YOK ve
  // OKUNMAMALI — aksi halde franchise faturaları yanlış (ana firma) bilgilerle gösterilir.
  const hasSellerSnapshot = !!invoice?.sellerCompanyName && invoice.sellerCompanyName.trim() !== '';

  useEffect(() => {
    if (!open) {
      setFetchedBranchInfo(null);
      setLoadingBranchInfo(false);
      setBranchInfoError(null);
      return;
    }

    // ✅ Yeni faturalar: snapshot kullanılır → Firebase'e gitmeye gerek yok
    if (hasSellerSnapshot) {
      console.log('🏪 [INVOICE MODAL] Seller snapshot kullanılıyor:', {
        sellerCompanyName: invoice?.sellerCompanyName,
        sellerSource: invoice?.sellerSource,
        isFranchiseInvoice: invoice?.isFranchiseInvoice,
      });
      setFetchedBranchInfo(null);
      setLoadingBranchInfo(false);
      setBranchInfoError(null);
      return;
    }

    // ⚠️ Eski faturalar (snapshot'sız): Firebase'den global firma bilgisi fallback
    if (!companyId) return;
    setLoadingBranchInfo(true);
    setBranchInfoError(null);
    setFetchedBranchInfo(null);
    getCompanyInfo(companyId)
      .then((info) => {
        if (info) {
          setFetchedBranchInfo(info as BranchInfoData);
        } else {
          setBranchInfoError(tt("Firma bilgileri bulunamadı.", "Company information not found."));
          setFetchedBranchInfo({ name: tt('Firma Bilgileri Yok', 'No Company Information') });
        }
      })
      .catch((err) => {
        console.error("Firma bilgileri (info) yüklenirken hata:", err);
        setBranchInfoError(tt("Firma bilgileri yüklenirken bir hata oluştu.", "An error occurred while loading company information."));
        setFetchedBranchInfo({ name: tt('Hata oluştu', 'An error occurred') });
      })
      .finally(() => {
        setLoadingBranchInfo(false);
      });
  }, [open, companyId, hasSellerSnapshot, invoice?.sellerCompanyName]); // open, companyId ve snapshot durumu

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              {tt('Fatura detayları yükleniyor...', 'Loading invoice details...')}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!invoice) {
    return null;
  }
  
  console.log('🔍 [FATURA MODAL] Fatura Detayları:', {
    invoiceNumber: invoice.invoiceNumber,
    type: invoice.type,
    grandTotal: invoice.grandTotal,
    originalInvoiceTotal: invoice.originalInvoiceTotal,
    currency: invoice.currency,
    itemCount: invoice.items?.length ?? 0,
  });

  // Fatura tipi adını al
  const getInvoiceTypeName = (type: InvoiceData['type']): string => {
    switch (type) {
      case 'sales':
        return tt('SATIŞ FATURASI', 'SALES INVOICE');
      case 'purchase':
        return tt('ALIŞ FATURASI', 'PURCHASE INVOICE');
      case 'quick_sale':
        return tt('HIZLI SATIŞ FATURASI (KAPALI)', 'QUICK SALES INVOICE (CLOSED)');
      default:
        const exhaustiveCheck: never = type; // type'ın tüm durumlarının ele alındığını kontrol eder
        return tt('FATURA', 'INVOICE');
    }
  };
  
  // ✅ Faturanın kendi seller snapshot'ı varsa onu kullan (yeni faturalar — PR1 sonrası).
  // Bu sayede franchise şubelerden kesilen faturalar fişte/modalda doğru firma bilgisini gösterir.
  // Snapshot yoksa Firebase'den çekilen global firma bilgisi fallback olarak kullanılır.
  const snapshotBranchInfo: BranchInfoData | null = hasSellerSnapshot
    ? {
        name: invoice.sellerCompanyName || '',
        taxNumber: invoice.sellerTaxNumber || undefined,
        taxOffice: invoice.sellerTaxOffice || undefined,
        address: invoice.sellerAddress || undefined,
        phone: invoice.sellerPhone || undefined,
        email: invoice.sellerEmail || undefined,
        mersisNo: invoice.sellerMersisNo || undefined,
        tradeRegistryNo: invoice.sellerTradeRegistryNo || undefined,
        // bankAccounts snapshot'ta tutulmuyor — Firebase'den de çekmiyoruz çünkü
        // franchise şubeler ana firmanın IBAN'ını GÖSTERMEMELİ. Banka bilgisi gerekirse
        // ileride Branches.Firm* alanlarına eklenmelidir.
      }
    : null;

  const displayBranchInfo = snapshotBranchInfo ?? fetchedBranchInfo;

  // Fatura kalemlerindeki item.total ve item.price alanlarını kontrol edelim.
  // Invoice.tsx'de `items: any[]` olarak tanımlı, bu yüzden gelen item yapısı değişken olabilir.
  // Burada `InvoiceItem` arayüzüne göre map'lemeye çalışacağız.
  // Gerekirse Invoice.tsx'deki item yapısına göre bu mapping güncellenmeli.
  const mappedItems = invoice.items.map(item => ({
    itemName: item.itemName || (item as any).name || tt('Bilinmeyen Ürün', 'Unknown Product'),
    quantity: item.quantity || 0,
    unit: item.unit || tt('Adet', 'Unit'),
    unitPrice: item.unitPrice || (item as any).price || 0,
    vatRate: item.vatRate || 0,
    sctRate: item.sctRate,
    discountRate: item.discountRate,
    // lineTotal: item.lineTotal || ((item.quantity || 0) * (item.unitPrice || (item as any).price || 0)), // Basit hesaplama, indirimler hariç
    // vatAmount: item.vatAmount || 0, // Bu bilgi gelmiyorsa hesaplanmalı
    grossTotal: item.grossTotal || (item as any).total || 0, // Genellikle item.total bu değeri tutar
  }));


  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth 
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2 } }}
    >
      <GlobalStyles styles={printStyles} />
      <DialogTitle sx={{ 
        backgroundColor: 'primary.main', 
        color: 'white', 
        p: { xs: 1, md: 1.5 }, 
        px: { xs: 2, md: 2.5 } 
      }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant={isMobile ? "subtitle1" : "h6"} component="div" sx={{ fontWeight: 'bold' }}>
            {getInvoiceTypeName(invoice.type)}
          </Typography>
          {extraHeaderContent && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {extraHeaderContent}
            </Box>
          )}
        </Grid>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, backgroundColor: '#f9f9f9' }}>
        <Box 
          id="printableInvoiceContent" 
          sx={{ 
            p: { xs: '10px', md: '20px' },
            m: 'auto',
            width: { xs: '100%', md: '210mm' },
            minHeight: { xs: 'auto', md: '297mm' },
            backgroundColor: 'white',
            boxShadow: { xs: 'none', md: '0 0 10px rgba(0,0,0,0.1)' }
          }}
        >
          {/* Fatura Başlığı ve Numarası - Responsive */}
          <Grid container spacing={{ xs: 2, md: 0 }} justifyContent="space-between" alignItems="flex-start" sx={{ mb: { xs: 2, md: 3 } }}>
            <Grid item xs={12} md={7}>
              {loadingBranchInfo ? (
                <CircularProgress size={20} />
              ) : displayBranchInfo ? (
                <>
                  <Typography variant={isMobile ? "subtitle1" : "h5"} component="div" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                    {displayBranchInfo.name}
                  </Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} display="block">
                    {displayBranchInfo.address}
                  </Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} display="block">
                    <strong>{tt('Vergi Dairesi', 'Tax Office')}:</strong> {displayBranchInfo.taxOffice} / <strong>{tt('V.K.N', 'Tax No')}</strong>: {displayBranchInfo.taxNumber}
                  </Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} display="block">
                    {tt('Tel', 'Phone')}: {displayBranchInfo.phone} {displayBranchInfo.email && `| ${tt('E-posta', 'Email')}: ${displayBranchInfo.email}`}
                  </Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} display="block">
                    {tt('Mersis No', 'Mersis No')}: {displayBranchInfo.mersisNo} | {tt('Tic. Sic. No', 'Trade Reg. No')}: {displayBranchInfo.tradeRegistryNo}
                  </Typography>
                </>
              ) : (
                <Typography color="error">{branchInfoError || tt("Firma bilgileri yüklenemedi.", "Company information could not be loaded.")}</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' }, border: '1px solid #eee', p: 1, borderRadius: 1 }}>
              <Typography variant={isMobile ? "subtitle2" : "h6"} sx={{ fontWeight: 'bold', color: 'black' }}>{tt('FATURA', 'INVOICE')}</Typography>
              <Typography variant={isMobile ? "body2" : "subtitle1"} component="div" sx={{ fontWeight: 'bold', color: 'error.main', fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                {invoice.invoiceNumber}
              </Typography>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant={isMobile ? "caption" : "body2"}><strong>{tt('Fatura Tarihi', 'Invoice Date')}:</strong> {formatDate(invoice.date, localeCode)}</Typography>
              {invoice.dueDate && <Typography variant={isMobile ? "caption" : "body2"}><strong>{tt('Vade Tarihi', 'Due Date')}:</strong> {formatDate(invoice.dueDate, localeCode)}</Typography>}
              <Typography variant={isMobile ? "caption" : "body2"}><strong>{tt('Düzenleme Saati', 'Issue Time')}:</strong> {formatDateTime(invoice.createdAt, localeCode)}</Typography>
            </Grid>
          </Grid>

          {/* Cari Bilgileri - Responsive */}
          <Paper variant="outlined" sx={{ p: { xs: 1, md: 1.5 }, mb: 2, backgroundColor: '#fafafa' }}>
            <Typography variant={isMobile ? "caption" : "subtitle1"} sx={{ fontWeight: 'bold', mb: 0.5 }}>{tt('ALICI BİLGİLERİ', 'BUYER INFORMATION')}</Typography>
            <Typography variant={isMobile ? "body2" : "body1"} sx={{ fontWeight: 'bold' }}>{invoice.account.companyName}</Typography>
            {invoice.account.taxNumber && <Typography variant={isMobile ? "caption" : "body2"}><strong>{tt('V.K.N / T.C. Kimlik No', 'Tax No / National ID')}:</strong> {invoice.account.taxNumber}</Typography>}
            {invoice.account.taxOffice && <Typography variant={isMobile ? "caption" : "body2"}><strong>{tt('Vergi Dairesi', 'Tax Office')}:</strong> {invoice.account.taxOffice}</Typography>}
            {invoice.account.address && <Typography variant={isMobile ? "caption" : "body2"}><strong>{tt('Adres', 'Address')}:</strong> {invoice.account.address}</Typography>}
            {invoice.account.phone && <Typography variant={isMobile ? "caption" : "body2"}><strong>{tt('Telefon', 'Phone')}:</strong> {invoice.account.phone}</Typography>}
            {invoice.account.email && <Typography variant={isMobile ? "caption" : "body2"}><strong>{tt('E-posta', 'Email')}:</strong> {invoice.account.email}</Typography>}
          </Paper>
          
          {invoice.description && (
            <Box sx={{ my: 2, p: 1, border: '1px dashed #ddd', borderRadius: 1, backgroundColor: 'info.lightest' }}>
              <Typography variant={isMobile ? "caption" : "body2"}><strong>{tt('Fatura Açıklaması', 'Invoice Description')}:</strong> {invoice.description}</Typography>
            </Box>
          )}

          {/* Ürün/Hizmet Tablosu - Responsive */}
          {isMobile ? (
            // Mobil: Kart Görünümü
            <Box sx={{ mb: 2 }}>
              {mappedItems.map((item, index) => (
                <Paper 
                  key={index}
                  variant="outlined" 
                  sx={{ 
                    p: 1.5, 
                    mb: 1.5,
                    backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">#{index + 1}</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      {formatCurrency(item.grossTotal, invoice.currency, localeCode).replace(invoice.currency, '').trim()} {invoice.currency}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    {item.itemName}
                  </Typography>
                  
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{tt('Miktar', 'Quantity')}:</Typography>
                      <Typography variant="body2">{item.quantity.toLocaleString(localeCode)} {item.unit}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{tt('Birim Fiyat', 'Unit Price')}:</Typography>
                      <Typography variant="body2">{formatCurrency(item.unitPrice, invoice.currency, localeCode).replace(invoice.currency, '').trim()}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{tt('KDV', 'VAT')}:</Typography>
                      <Typography variant="body2">{item.vatRate}%</Typography>
                    </Grid>
                    {item.discountRate && item.discountRate > 0 && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">{tt('İskonto', 'Discount')}:</Typography>
                        <Typography variant="body2">{item.discountRate}%</Typography>
                      </Grid>
                    )}
                    {item.sctRate && item.sctRate > 0 && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">{tt('ÖTV', 'SCT')}:</Typography>
                        <Typography variant="body2">{item.sctRate}%</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))}
            </Box>
          ) : (
            // Desktop: Tablo Görünümü
            <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small" aria-label={tt('fatura kalemleri tablosu', 'invoice items table')}>
              <TableHead sx={{backgroundColor: '#f0f0f0'}}>
                <TableRow>
                  <StyledTableHeadCell sx={{width: '30px'}}>#</StyledTableHeadCell>
                  <StyledTableHeadCell>{tt('Mal / Hizmet', 'Product / Service')}</StyledTableHeadCell>
                  <StyledTableHeadCell align="right" sx={{width: '70px'}}>{tt('Miktar', 'Quantity')}</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{width: '60px'}}>{tt('Birim', 'Unit')}</StyledTableHeadCell>
                  <StyledTableHeadCell align="right" sx={{width: '100px'}}>{tt('Birim Fiyat', 'Unit Price')}</StyledTableHeadCell>
                  {mappedItems.some(item => typeof item.discountRate === 'number' && item.discountRate > 0) && (
                    <StyledTableHeadCell align="right" sx={{width: '60px'}}>{tt('İsk. (%)', 'Disc. (%)')}</StyledTableHeadCell>
                  )}
                  <StyledTableHeadCell align="right" sx={{width: '60px'}}>{tt('KDV (%)', 'VAT (%)')}</StyledTableHeadCell>
                  {mappedItems.some(item => typeof item.sctRate === 'number' && item.sctRate > 0) && (
                    <StyledTableHeadCell align="right" sx={{width: '60px'}}>{tt('ÖTV (%)', 'SCT (%)')}</StyledTableHeadCell>
                  )}
                  <StyledTableHeadCell align="right" sx={{width: '110px'}}>{tt('Tutar', 'Amount')} ({invoice.currency})</StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mappedItems.map((item, index) => (
                  <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <StyledTableCell component="th" scope="row">{index + 1}</StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: '500' }}>{item.itemName}</StyledTableCell>
                    <StyledTableCell align="right">{item.quantity.toLocaleString(localeCode)}</StyledTableCell>
                    <StyledTableCell>{item.unit}</StyledTableCell>
                    <StyledTableCell align="right">{formatCurrency(item.unitPrice, invoice.currency, localeCode).replace(invoice.currency, '').trim()}</StyledTableCell>
                    {mappedItems.some(i => typeof i.discountRate === 'number' && i.discountRate > 0) && (
                        <StyledTableCell align="right">{item.discountRate || 0}%</StyledTableCell>
                    )}
                    <StyledTableCell align="right">{item.vatRate}%</StyledTableCell>
                    {mappedItems.some(i => typeof i.sctRate === 'number' && i.sctRate > 0) && (
                       <StyledTableCell align="right">{item.sctRate || 0}%</StyledTableCell>
                    )}
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(item.grossTotal, invoice.currency, localeCode).replace(invoice.currency, '').trim()}</StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          {/* Toplamlar Bölümü - Responsive */}
          <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={12} md={6} lg={5}>
              <TableContainer component={Paper} variant='outlined'>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <StyledTableCell>{tt('ARA TOPLAM', 'SUBTOTAL')}</StyledTableCell>
                      <StyledTableCell align="right">{formatCurrency(invoice.subtotal, invoice.currency, localeCode)}</StyledTableCell>
                    </TableRow>
                    {typeof invoice.discountTotal === 'number' && invoice.discountTotal > 0 && (
                      <TableRow>
                        <StyledTableCell sx={{ color: 'error.main' }}>{tt('GENEL İSKONTO', 'TOTAL DISCOUNT')}</StyledTableCell>
                        <StyledTableCell align="right" sx={{ color: 'error.main' }}>-{formatCurrency(invoice.discountTotal, invoice.currency, localeCode)}</StyledTableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <StyledTableCell>{tt('TOPLAM KDV', 'TOTAL VAT')}</StyledTableCell>
                      <StyledTableCell align="right">{formatCurrency(invoice.vatTotal, invoice.currency, localeCode)}</StyledTableCell>
                    </TableRow>
                    {typeof invoice.sctTotal === 'number' && invoice.sctTotal > 0 && (
                      <TableRow>
                        <StyledTableCell>{tt('TOPLAM ÖTV', 'TOTAL SCT')}</StyledTableCell>
                        <StyledTableCell align="right">{formatCurrency(invoice.sctTotal, invoice.currency, localeCode)}</StyledTableCell>
                      </TableRow>
                    )}

                    {/* ✅ Tevkifat kesintisi */}
                    {typeof invoice.tevkifatAmount === 'number' && invoice.tevkifatAmount > 0 && (
                      <TableRow>
                        <StyledTableCell sx={{ color: 'error.main' }}>
                          {tt('KDV TEVKİFATI', 'VAT WITHHOLDING')}{invoice.tevkifatRate ? ` (${invoice.tevkifatRate})` : ''}
                        </StyledTableCell>
                        <StyledTableCell align="right" sx={{ color: 'error.main' }}>-{formatCurrency(invoice.tevkifatAmount, invoice.currency, localeCode)}</StyledTableCell>
                      </TableRow>
                    )}

                    {/* ✅ Gelir vergisi stopajı */}
                    {typeof invoice.stopajAmount === 'number' && invoice.stopajAmount > 0 && (
                      <TableRow>
                        <StyledTableCell sx={{ color: 'error.main' }}>
                          {tt('GELİR VERGİSİ STOPAJI', 'INCOME TAX WITHHOLDING')}{invoice.stopajRate ? ` (%${invoice.stopajRate})` : ''}
                        </StyledTableCell>
                        <StyledTableCell align="right" sx={{ color: 'error.main' }}>-{formatCurrency(invoice.stopajAmount, invoice.currency, localeCode)}</StyledTableCell>
                      </TableRow>
                    )}

                    {/* ✅ YENİ: Orijinal fatura tutarı varsa farkı göster */}
                    {invoice.originalInvoiceTotal && invoice.originalInvoiceTotal !== invoice.grandTotal && (
                      <>
                        <TableRow>
                          <StyledTableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{tt('Hesaplanan Toplam', 'Calculated Total')}</StyledTableCell>
                          <StyledTableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{formatCurrency(invoice.grandTotal, invoice.currency, localeCode)}</StyledTableCell>
                        </TableRow>
                        <TableRow>
                          <StyledTableCell sx={{
                            fontSize: '0.75rem',
                            color: (invoice.originalInvoiceTotal - invoice.grandTotal) > 0 ? 'success.main' : 'error.main',
                            fontWeight: 'medium'
                          }}>
                            {tt('Fatura Farkı (Yuvarlama)', 'Invoice Difference (Rounding)')}
                          </StyledTableCell>
                          <StyledTableCell align="right" sx={{
                            fontSize: '0.75rem',
                            color: (invoice.originalInvoiceTotal - invoice.grandTotal) > 0 ? 'success.main' : 'error.main',
                            fontWeight: 'medium'
                          }}>
                            {(invoice.originalInvoiceTotal - invoice.grandTotal) > 0 ? '+' : ''}{formatCurrency(invoice.originalInvoiceTotal - invoice.grandTotal, invoice.currency, localeCode)}
                          </StyledTableCell>
                        </TableRow>
                      </>
                    )}
                    
                    <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                      <StyledTableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.8rem', md: '0.9rem' } }}>{tt('GENEL TOPLAM', 'GRAND TOTAL')}</StyledTableCell>
                      <StyledTableCell align="right" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                        {formatCurrency(invoice.originalInvoiceTotal || invoice.grandTotal, invoice.currency, localeCode)}
                      </StyledTableCell>
                    </TableRow>
                    {/* ✅ TRY dışı faturada kayıtlı kur + TL karşılığı (kaç TL çekildi/borçlandı).
                        Kur 1 ise gösterilmez (eski/kursuz kayıtlar — yanıltıcı olur). */}
                    {invoice.currency && invoice.currency.toUpperCase() !== 'TRY' && (invoice.exchangeRate || 0) > 0 && invoice.exchangeRate !== 1 && (
                      <>
                        <TableRow>
                          <StyledTableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {tt('DÖVİZ KURU', 'EXCHANGE RATE')}
                          </StyledTableCell>
                          <StyledTableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            1 {invoice.currency.toUpperCase()} = {(invoice.exchangeRate as number).toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ₺
                          </StyledTableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#fff8e1' }}>
                          <StyledTableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.8rem', md: '0.9rem' } }}>{tt('TL KARŞILIĞI', 'TRY EQUIVALENT')}</StyledTableCell>
                          <StyledTableCell align="right" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                            {formatCurrency((invoice.originalInvoiceTotal || invoice.grandTotal) * (invoice.exchangeRate as number), 'TRY', localeCode)}
                          </StyledTableCell>
                        </TableRow>
                      </>
                    )}
                    {/* ✅ Ödenecek Tutar (tevkifat/stopaj kesintisi varsa) */}
                    {((invoice.tevkifatAmount || 0) > 0 || (invoice.stopajAmount || 0) > 0) && (
                      <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
                        <StyledTableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.8rem', md: '0.9rem' } }}>{tt('ÖDENECEK TUTAR', 'PAYABLE AMOUNT')}</StyledTableCell>
                        <StyledTableCell align="right" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', md: '1rem' }, color: 'success.main' }}>
                          {formatCurrency((invoice.originalInvoiceTotal || invoice.grandTotal) - (invoice.tevkifatAmount || 0) - (invoice.stopajAmount || 0), invoice.currency, localeCode)}
                        </StyledTableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
          
          {/* Yazı ile Tutar */}
          <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontStyle: 'italic', color: 'text.primary' }}>
              <strong>{tt('Yalnız', 'Only')}:</strong> {amountToText(invoice.originalInvoiceTotal || invoice.grandTotal, invoice.currency, isTr, localeCode)}
            </Typography>
          </Box>


          {(invoice.notes || (displayBranchInfo?.bankAccounts && displayBranchInfo.bankAccounts.length > 0)) && (
            <Paper variant="outlined" sx={{ p: { xs: 1, md: 1.5 }, mt: 2, backgroundColor: '#fafafa' }}>
              {invoice.notes && (
                <Box sx={{ mb: displayBranchInfo?.bankAccounts && displayBranchInfo.bankAccounts.length > 0 ? 1.5 : 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>{tt('Fatura Notları', 'Invoice Notes')}:</Typography>
                  <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-wrap' }}>
                    {invoice.notes}
                  </Typography>
                </Box>
              )}
              {displayBranchInfo?.bankAccounts && displayBranchInfo.bankAccounts.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>{tt('Banka Hesap Bilgileri', 'Bank Account Information')}:</Typography>
                  {displayBranchInfo.bankAccounts.map((acc, idx) => (
                    <Typography key={idx} variant="caption" display="block">
                      <strong>{acc.bankName}:</strong> {acc.iban}
                    </Typography>
                  ))}
                </Box>
              )}
            </Paper>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" display="block" sx={{ textAlign: 'center', color: 'text.secondary', fontSize: { xs: '0.6rem', md: '0.65rem' } }}>
            {tt('İşbu fatura .... sayılı V.U.K. Genel Tebliği uyarınca elektronik ortamda düzenlenmiştir. / Kağıt çıktısıdır.', 'This invoice has been issued electronically in accordance with the General Communique on Tax Procedure Law. / Paper printout.')}
            <br />
            {tt('Fatura Detayları İçin QR Kodu (Opsiyonel)', 'QR Code for Invoice Details (Optional)')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: { xs: 1, md: 1.5 }, backgroundColor: 'grey.100', borderTop: '1px solid #ddd', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 0 } }}>
        <Button 
          fullWidth={isMobile}
          onClick={onClose} 
          variant="outlined" 
          color="primary"
        >
          {tt('Kapat', 'Close')}
        </Button>
        <Button 
          fullWidth={isMobile}
          onClick={() => { 
            window.print(); 
          }} 
          variant="contained" 
          color="secondary"
        >
          {tt('Yazdır', 'Print')}
        </Button>
      </DialogActions>
      {/* Yazdırma işlemi için gizli bir alan (veya doğrudan DialogContent'in bir kısmını ID ile seç) */}
      {/* Bu yaklaşım yerine CSS @media print kullanmak daha iyi olabilir */}
      {/* <div id="invoice-print-area" style={{ display: 'none' }}> */}
         {/* DialogContent içeriği buraya kopyalanabilir veya referans alınabilir */}
         {/* Örnek: Belirli bir ID'ye sahip Box elemanını yazdırmak daha mantıklı. Üstteki Box'a id='printableInvoiceContent' verilebilir. */}
      {/* </div> */}
    </Dialog>
  );
};

export default InvoiceDisplayModal;
// Invoice.tsx'de kullanılmak üzere InvoiceData arayüzünü export edebiliriz.
// Ancak, Invoice.tsx'deki Invoice arayüzü zaten varsa ve export ediliyorsa onu kullanmak daha iyi.
// Şimdilik bu modal'a özel arayüzü burada bırakıyorum.
export type { InvoiceData }; // InvoiceData'yı export et

// Invoice.tsx'deki item yapısı (any[]) ile bu modaldeki InvoiceItem arasında bir dönüşüm gerekebilir.
// Bu dönüşüm ya Invoice.tsx'de modal çağrılırken ya da bu modal içinde yapılabilir.
// Invoice.tsx'deki item genellikle { name, quantity, price, vatRate, total, ... } gibi alanlara sahip olabilir. 